import type { StrictOmit } from "ts-essentials";
import * as z from "zod";
import type { SchemaShape, TableSchemaShape } from "~/components/Form";
import {
  boolean,
  commonResourceFilterSchemas,
  filterBoolean,
  filterNumber,
  filterText,
  filterUuid,
  optionalNumber,
  optionalObject,
  optionalText,
  optionalUuid,
  requiredNumber,
  requiredUuid,
} from "~/domain/common";
import type {
  IngestionCreateRequest,
  IngestionPartForIngestionCreateRequest,
  IngestionPartUpdateRequest,
  IngestionUpdateRequest,
  ListIngestionPartsForIngestionRequest,
  ListIngestionsRequest,
} from "~/lqs";
import { filterProcessState, requiredProcessState } from "~/lqs";

export const listIngestionsSchema = z.object({
  id: filterUuid,
  name: filterText,
  nameLike: filterText,
  logId: filterUuid,
  workflowId: filterUuid,
  workflowIdNull: filterBoolean,
  objectStoreId: filterUuid,
  objectKey: filterText,
  objectKeyLike: filterText,
  noteLike: filterText,
  progressGte: filterNumber,
  progressLte: filterNumber,
  progressNull: filterBoolean,
  state: filterProcessState,
  ...commonResourceFilterSchemas,
} satisfies TableSchemaShape<
  StrictOmit<
    ListIngestionsRequest,
    | "contextFilter"
    | "workflowContextFilter"
    | "errorFilter"
    | "errorPayloadFilter"
    | "groupId"
    | "includeCount"
  >
>);

export const createIngestionSchema = z.object({
  name: optionalText,
  logId: requiredUuid,
  objectStoreId: optionalUuid,
  objectKey: optionalText,
  state: requiredProcessState,
  workflowId: optionalUuid,
  workflowContext: optionalObject,
  locked: boolean,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<IngestionCreateRequest>);

export const editIngestionSchema = z.object({
  name: optionalText,
  objectStoreId: optionalUuid,
  objectKey: optionalText,
  state: requiredProcessState,
  progress: optionalNumber,
  workflowId: optionalUuid,
  workflowContext: optionalObject,
  error: optionalObject,
  errorName: optionalText,
  errorMessage: optionalText,
  errorPayload: optionalObject,
  locked: boolean,
  note: optionalText,
  context: optionalObject,
} satisfies SchemaShape<IngestionUpdateRequest>);

export const listIngestionPartsSchema = z.object({
  id: filterUuid,
  sequence: filterNumber,
  source: filterText,
  workflowId: filterUuid,
  workflowIdNull: filterBoolean,
  state: filterProcessState,
  ...commonResourceFilterSchemas,
} satisfies TableSchemaShape<
  StrictOmit<
    ListIngestionPartsForIngestionRequest,
    "ingestionId" | "groupId" | "logId" | "includeCount"
  >
>);

export const createIngestionPartSchema = z.object({
  sequence: requiredNumber,
  source: optionalText,
  state: requiredProcessState,
  workflowId: optionalUuid,
  workflowContext: optionalObject,
  locked: boolean,
  context: optionalObject,
} satisfies SchemaShape<
  StrictOmit<IngestionPartForIngestionCreateRequest, "index">
>);

export const editIngestionPartSchema = z.object({
  sequence: requiredNumber,
  source: optionalText,
  state: requiredProcessState,
  progress: optionalNumber,
  workflowId: optionalUuid,
  workflowContext: optionalObject,
  error: optionalObject,
  errorName: optionalText,
  errorMessage: optionalText,
  errorPayload: optionalObject,
  locked: boolean,
  context: optionalObject,
} satisfies SchemaShape<StrictOmit<IngestionPartUpdateRequest, "index">>);
